import { LOCALES } from '@/enums/locale'
import { MODULES, Module, ModuleRole } from '@/enums/module'
import i18n from '@/libs/i18n'
import type { SelectOption, TagItem } from '@/types/Common'
import type { IModules } from '@/types/User'

/** A module's name in the reader's own language (docs/API.md → UI labels). */
export const moduleLabel = (module: string): string => {
    switch (module) {
        case Module.Naloge: return i18n.global.t('module.naloge')
        default:            return module
    }
}

/** What a role inside a module is called: Vodja, Član. */
export const moduleRoleLabel = (role: string): string => {
    switch (role) {
        case ModuleRole.Manager: return i18n.global.t('module.role_manager')
        case ModuleRole.Member:  return i18n.global.t('module.role_member')
        default:                 return role
    }
}

/*
 * The badge of a module role. A manager — who sees everything in the module — carries the
 * information colour, a member, the ordinary case, stays neutral.
 */
export const getModuleRoleTag = (role: string): TagItem => role === ModuleRole.Manager
    ? { label: moduleRoleLabel(role), color: 'info' }
    : { label: moduleRoleLabel(role), color: 'neutral' }

/** The portal administrator's badge: the accent, the account that changes what others see. */
export const getPortalAdminTag = (): TagItem => ({ label: i18n.global.t('module.portal_admin'), color: 'primary' })

/*
 * One module's setting in the user dialog: off, or on with a role. "Off" is a real choice with
 * a value of its own (`MODULE_OFF`), because a select cannot pick an empty value.
 */
export const MODULE_OFF = 'off'

export const moduleRoleOptions = (): SelectOption[] => [
    { label: i18n.global.t('module.off'),  value: MODULE_OFF },
    { label: moduleRoleLabel(ModuleRole.Member),  value: ModuleRole.Member },
    { label: moduleRoleLabel(ModuleRole.Manager), value: ModuleRole.Manager },
]

/** The modules a person has, in menu order, each with its role. */
export const moduleEntries = (modules: IModules | null | undefined): { module: Module, role: string }[] =>
    MODULES
        .map((module) => ({ module, role: modules?.[module] ?? '' }))
        .filter((entry) => entry.role === ModuleRole.Manager || entry.role === ModuleRole.Member)

/*
 * WHAT A PERSON MAY USE, in one line — the second line of the user row in the sidebar:
 * "Skrbnik portala", or the modules with their roles ("Naloge: Vodja").
 */
export const accessLine = (portalAdmin: boolean, modules: IModules | null | undefined): string => {
    if (portalAdmin) return i18n.global.t('module.portal_admin')

    const entries = moduleEntries(modules)

    if (entries.length === 0) return i18n.global.t('module.none')

    return entries.map((entry) => `${moduleLabel(entry.module)}: ${moduleRoleLabel(entry.role)}`).join(' · ')
}

/** "Ana Novak" → "AN"; a username alone ("ana.novak") gives "AN" too. */
export const initials = (name: string): string => name
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

/** Usernames: 3–32 characters of a–z, 0–9, dot, underscore and dash (docs/API.md). */
export const USERNAME_PATTERN = /^[a-z0-9._-]{3,32}$/

export const MIN_PASSWORD = 8

/*
 * The two languages, each written in itself ("Slovenščina", "English"), so a person who
 * cannot read the current one still finds their own.
 */
export const localeOptions = (): SelectOption[] =>
    LOCALES.map((code) => ({ label: i18n.global.t(`locale.${code}`), value: code }))
